import Button from './base/Button';
import Box from './Box';
import { ReactComponent as LinkIcon } from '../assets/icons/link.svg';
import { useAppState } from '../state/StateContext';
import { __, formatPrice } from '../functions/commom';
import { CURRENCY, DATE_FORMAT } from '../config';
import moment from 'moment-timezone';
import { useParams, useSearchParams } from 'react-router-dom';
import { getInsuranceCalculate } from '../functions/insuranceSearchService';
import { createUseStyles } from 'react-jss';
import { useState, useEffect } from 'react';
import { InsuranceQuote } from '../types/insurance.types';

const useStyles = createUseStyles({
  header: {
    marginTop: '20px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  subLine: {
    fontSize: '16px',
    marginBottom: '5px',
    color: '#666',
  },
  subTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  backButton: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1px solid #ebebeb',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '20px',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
  content: {
    fontSize: '16px',
    lineHeight: '1.5',
  },
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  selectingSummary: {
    cursor: 'pointer',
    display: 'flex',
    fontSize: '14px',
    fontWeight: '400',
    color: '#666666',
    marginTop: '5px',
  },
  items: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateRows: 'auto',
    gap: '20px',
    '& .item': {
      '&.first': {
        gridColumn: '1 / 3',
      },
    },
    '& .item-name': {
      fontSize: '14px',
      color: '#666666',
      marginBottom: '5px',
      fontWeight: '300',
    },
  },
});

// http://localhost:3000/insurance/travel/single_trip/basic?departureDate=2025-03-19&returnDate=2025-03-21&coverageType=individual&withCruise=false
function InsuranceFormLeftInformation() {
  const {
    id = 'single_trip',
    tier = 'basic',
    category = 'travel',
  } = useParams<{
    id?: string;
    tier?: string;
    category?: string;
  }>();

  const classes = useStyles();
  const state = useAppState();
  const [searchParams] = useSearchParams();

  // For travel insurance
  const departureDate = searchParams.get('departureDate');
  const returnDate = searchParams.get('returnDate');
  const coverageType = searchParams.get('coverageType');
  const withCruise = searchParams.get('withCruise');
  const couponCode = searchParams.get('couponCode');

  // For home insurance
  const areaType = searchParams.get('areaType') || 'net';
  const floorArea = searchParams.get('floorArea') || '0';
  const buildingAge = searchParams.get('buildingAge') || '0';

  const withCruiseBoolean = withCruise === 'true';
  const [insuranceQuoteResult, setInsuranceQuoteResult] =
    useState<InsuranceQuote | null>(null);

  const insurancePlans = state?.insurancePlans?.get();
  const selectedInsurance = Array.isArray(insurancePlans)
    ? insurancePlans.find((plan) => plan.id === id)
    : undefined;

  const selectedTier = insuranceQuoteResult
    ? insuranceQuoteResult?.quotes?.find((quote) => quote.id === tier)
    : null;

  let TravelItems = [
    {
      name: __('Type'),
      type: 'text',
      value:
        selectedInsurance?.category === 'travel'
          ? __('Travel Insurance')
          : __('Home Insurance'),
    },
    {
      name: __('Coverage'),
      type: 'text',
      value: coverageType ? __(coverageType) : null,
    },
    {
      name: __('Currency'),
      type: 'text',
      value: CURRENCY,
    },
    {
      name: selectedInsurance?.isAnnualPlan
        ? __('Effective Date')
        : __('Departure Date'),
      type: 'date',
      value: moment(departureDate).format(DATE_FORMAT) || null,
    },
    {
      name: selectedInsurance?.isAnnualPlan
        ? __('End Date')
        : __('Return Date'),
      type: 'date',
      value: selectedInsurance?.isAnnualPlan
        ? moment(departureDate).add(364, 'day').format(DATE_FORMAT)
        : moment(returnDate).format(DATE_FORMAT) || null,
    },
    {
      name: __('Cruise Extension'),
      type: 'text',
      value: withCruiseBoolean ? __('Included') : null,
    },
    {
      name: __('Policyholder'),
      type: 'text',
      value: state.form.name.get() || null,
    },
    {
      name: __('Amounts of Insured'),
      type: 'text',
      value:
        state.form.insuredPersons.length > 0
          ? state.form.insuredPersons.length
          : null,
    },
    {
      name: __('Premium'),
      type: 'text',
      value:
        formatPrice(
          selectedTier?.discount
            ? selectedTier?.discount
            : selectedTier?.premium || 0,
          '$'
        ) + (selectedInsurance?.isAnnualPlan ? '/' + __('Year') : ''),
    },
    {
      name: __('Coupon Code'),
      type: 'text',
      value: selectedTier?.discount ? couponCode : null,
    },
  ];

  // for home insurance
  let HomeItems = [
    {
      name: __('Type'),
      type: 'text',
      value:
        selectedInsurance?.category === 'travel'
          ? __('Travel Insurance')
          : __('Home Insurance'),
    },
    {
      name: __('Currency'),
      type: 'text',
      value: CURRENCY,
    },
    {
      name: selectedInsurance?.isAnnualPlan
        ? __('Effective Date')
        : __('Departure Date'),
      type: 'date',
      value: moment(departureDate).format(DATE_FORMAT) || null,
    },
    {
      name: __('End Date'),
      type: 'date',
      value: moment(departureDate).add(364, 'day').format(DATE_FORMAT) || null,
    },
    {
      name: __('Area Calculation'),
      type: 'text',
      value: __(areaType),
    },
    {
      name: __('Total Area (sqft)'),
      type: 'text',
      value: floorArea || null,
    },
    {
      name: __('Building Age'),
      type: 'text',
      value: buildingAge || null,
    },
    {
      name: __('Policyholder'),
      type: 'text',
      value: state.form.name.get() || null,
    },
    {
      name: __('Amounts of Insured'),
      type: 'text',
      value:
        state.form.insuredPersons.length > 0
          ? state.form.insuredPersons.length
          : null,
    },
    {
      name: __('Premium'),
      type: 'text',
      value: formatPrice(
        selectedTier?.discount
          ? selectedTier?.discount
          : selectedTier?.premium || 0,
        '$'
      ),
    },
    {
      name: __('Coupon Code'),
      type: 'text',
      value: selectedTier?.discount ? couponCode : null,
    },
  ];

  const items =
    selectedInsurance?.category === 'travel' ? TravelItems : HomeItems;

  function FormFooter() {
    return (
      <Button
        theme="simple"
        to={selectedInsurance?.landingUrl}
        target="_blank"
        icon={<LinkIcon />}
        iconPosition="after"
      >
        {__('Visit Details Page')}
      </Button>
    );
  }

  useEffect(() => {
    const fetchInsuranceQuote = async () => {
      try {
        const result = await getInsuranceCalculate({
          id: id || 'single_trip',
          departureDate: moment(departureDate).format(DATE_FORMAT) || undefined,
          returnDate: returnDate
            ? moment(returnDate).format(DATE_FORMAT)
            : undefined,
          coverageType: coverageType || 'individual',
          category: category,
          tier: tier,
          withCruise: withCruiseBoolean,
          couponCode: couponCode || undefined,
          floorArea: parseInt(floorArea || '0'),
          areaType: areaType === 'net' ? 'net' : 'gross',
          buildingAge: parseInt(buildingAge || '0'),
        });

        setInsuranceQuoteResult(result);
      } catch (error) {
        state?.isError?.set(true);
        state?.errorMessage?.set(
          error instanceof Error ? error.message : 'Unknown error occurred'
        );
        console.error('Error fetching insurance quote:', error);
      }
    };

    fetchInsuranceQuote();
  }, [
    id,
    tier,
    departureDate,
    returnDate,
    coverageType,
    category,
    withCruiseBoolean,
    couponCode,
    floorArea,
    buildingAge,
    areaType,
    state,
  ]);

  return (
    <>
      <Box
        header={__('Insurance Information')}
        footer={selectedInsurance?.landingUrl && <FormFooter />}
      >
        <>
          <div className={classes.items}>
            <div className="item first">
              <div className="item-name">{__('Plan Name')}</div>
              <div className="item-value">
                {/* Only show tier name for single_trip plan, otherwise show plan name */}
                {id === 'single_trip'
                  ? __(selectedInsurance?.name) +
                    ' - ' +
                    __(selectedTier?.name || '')
                  : __(selectedInsurance?.name)}
              </div>
            </div>
            {items.map(
              (item, index) =>
                item?.value && (
                  <div className="item" key={index}>
                    <div className="item-name">{__(item.name)}</div>
                    <div className="item-value">
                      {item.type === 'price'
                        ? formatPrice(
                            typeof item.value === 'number'
                              ? item.value
                              : Number(item.value) || undefined,
                            '$'
                          )
                        : item.type === 'date'
                        ? moment(item?.value || null).format(DATE_FORMAT)
                        : item.value}
                    </div>
                  </div>
                )
            )}
          </div>
          <hr style={{ margin: '20px 0' }} />
          <div className={classes.subTitle}>{__('Insurance Highlights')}</div>
          <div className={classes.items}>
            {selectedTier?.highlightItems?.map((item, index) => (
              <div className="item" key={index}>
                <div className="item-name">{__(item.name)}</div>
                <div className="item-value">
                  {item.type === 'price'
                    ? formatPrice(
                        typeof item.value === 'number'
                          ? item.value
                          : Number(item.value) || undefined,
                        '$'
                          )
                    : item.type === 'date'
                    ? moment(item?.value || null).format(DATE_FORMAT)
                    : item.value}
                </div>
              </div>
            ))}
          </div>
        </>
      </Box>
    </>
  );
}

export default InsuranceFormLeftInformation;
